import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MapPin, Ruler, Lightbulb, Package, Wrench, CheckCircle2 } from "lucide-react";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Btn, Overline } from "@/components/Btn";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CtaBand } from "@/components/CtaBand";
import { getRealisation } from "@/data/realisations";
import { getService } from "@/data/services";
import { getLocale } from "@/data/locales";
import { COMPANY } from "@/data/company";

const specIcon = { dimensions: Ruler, lighting: Lightbulb, materials: Package };

export default function RealisationDetail() {
  const { slug } = useParams();
  const r = getRealisation(slug);
  if (!r) return <Navigate to="/404" replace />;

  const path = `/realisations/${r.slug}/`;
  const crumbs = [
    { label: "Accueil", to: "/" },
    { label: "Réalisations", to: "/realisations/" },
    { label: r.city, to: path },
  ];
  const loc = getLocale(r.locale);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: r.title,
    about: r.type,
    image: r.gallery,
    locationCreated: { "@type": "Place", name: r.city },
    description: r.solution,
    mainEntityOfPage: COMPANY.siteUrl + path,
  };

  const blocks = [
    { label: "Problématique", text: r.problem },
    { label: "Solution proposée", text: r.solution },
    { label: "Fabrication", text: r.fabrication },
    { label: "Installation", text: r.installation },
    { label: "Résultat", text: r.result },
  ];

  return (
    <>
      <Seo title={`${r.title} | Réalisation – Ronalp Enseigne`} description={r.solution} path={path} image={r.cover} type="article" jsonLd={[jsonLd, breadcrumbSchema(crumbs)]} />

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <Breadcrumb items={crumbs} />
        <Reveal className="mt-8 max-w-3xl">
          <Overline>{r.type} • {r.city}</Overline>
          <h1 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-white leading-[1]">{r.title}</h1>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl border border-white/10">
            <img src={r.cover} alt={r.alt} className="w-full h-[320px] sm:h-[480px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-900/50 to-transparent" />
          </div>
        </Reveal>
        {r.gallery.length > 1 && (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-5">
            {r.gallery.slice(1).map((g, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <img src={g} alt={`${r.alt} — vue ${i + 2}`} className="h-48 w-full object-cover rounded-xl border border-white/10" loading="lazy" />
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-10">
            {blocks.map((b) => (
              <Reveal key={b.label}>
                <h2 className="text-xl sm:text-2xl font-bold text-white">{b.label}</h2>
                <p className="mt-3 text-slate-300 leading-relaxed">{b.text}</p>
              </Reveal>
            ))}
            <Reveal>
              <h2 className="text-xl sm:text-2xl font-bold text-white">Services utilisés</h2>
              <div className="mt-4 flex flex-wrap gap-3">
                {r.services.map((sl) => {
                  const svc = getService(sl);
                  if (!svc) return null;
                  return (
                    <Link key={sl} to={`/${svc.slug}/`} className="group inline-flex items-center gap-2 rounded-lg border border-white/10 bg-ink-800 px-4 py-2.5 text-sm text-slate-300 hover:border-signature/40 hover:text-white transition-all" data-testid={`realisation-service-${sl}`}>
                      <Wrench className="h-4 w-4 text-signature" /> {svc.nav}
                      <ArrowUpRight className="h-3.5 w-3.5 text-chrome-dark group-hover:text-signature" />
                    </Link>
                  );
                })}
              </div>
            </Reveal>
          </div>

          <aside>
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="rounded-2xl border border-white/10 bg-ink-800 p-7">
                <h3 className="text-sm font-mono uppercase tracking-wider text-signature mb-5">Fiche technique</h3>
                <dl className="space-y-4 text-sm">
                  <div className="flex items-start gap-3"><MapPin className="h-4 w-4 text-chrome-mid mt-0.5" /><div><dt className="text-chrome-dark">Ville</dt><dd className="text-white">{r.city}</dd></div></div>
                  <div className="flex items-start gap-3"><Ruler className="h-4 w-4 text-chrome-mid mt-0.5" /><div><dt className="text-chrome-dark">Dimensions</dt><dd className="text-white">{r.dimensions}</dd></div></div>
                  <div className="flex items-start gap-3"><Lightbulb className="h-4 w-4 text-chrome-mid mt-0.5" /><div><dt className="text-chrome-dark">Éclairage</dt><dd className="text-white">{r.lighting}</dd></div></div>
                  <div className="flex items-start gap-3"><Package className="h-4 w-4 text-chrome-mid mt-0.5" /><div><dt className="text-chrome-dark">Matériaux</dt><dd className="text-white">{r.materials.join(", ")}</dd></div></div>
                </dl>
              </div>
              {loc && (
                <Link to={`/${loc.slug}/`} className="group flex items-center justify-between rounded-2xl border border-white/10 bg-ink-800 p-6 hover:border-signature/40 transition-all" data-testid="realisation-locale-link">
                  <div><p className="text-xs text-chrome-dark font-mono uppercase">Zone</p><p className="text-white font-semibold group-hover:text-signature transition-colors">{loc.nav}</p></div>
                  <ArrowUpRight className="h-5 w-5 text-chrome-mid group-hover:text-signature" />
                </Link>
              )}
              <div className="rounded-2xl border border-signature/30 bg-gradient-to-b from-ink-800 to-ink-900 p-7 glow-red">
                <h3 className="text-lg font-bold text-white">Un projet similaire ?</h3>
                <Btn to="/contact/" className="mt-4 w-full">Demander un devis <ArrowRight className="h-4 w-4" /></Btn>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
